import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ResourceType } from "@common/types/auditLog";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { LogTimeCell } from "../../cells/LogTimeCell";
import {
    HistoryInvoiceTemplateChangeColumn,
    HistoryInvoiceTemplateContentColumn,
    HistoryInvoiceTemplateDetailsColumn,
} from "./historyInvoiceTemplateDetailsView";

interface HistoryInvoiceTemplateBaseRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    templateName: string;
}

export interface HistoryInvoiceTemplateChangeRow extends HistoryInvoiceTemplateBaseRow {
    changedFieldLabel: string | null;
    previous: string;
    modified: string;
}

export interface HistoryInvoiceTemplateContentRow extends HistoryInvoiceTemplateBaseRow {
    change: string;
}

const DEFAULT_PREVIOUS_LABEL = "Previous";
const DEFAULT_MODIFIED_LABEL = "Modified";

const getSingleFieldLabel = (
    data: HistoryInvoiceTemplateChangeRow[],
): string | null => {
    const fieldLabels = new Set(data.map((row) => row.changedFieldLabel));
    const [fieldLabel] = fieldLabels;

    return fieldLabels.size === 1 ? fieldLabel : null;
};

const baseColumns = {
    [HistoryInvoiceTemplateDetailsColumn.TIME]: {
        name: "Time",
        header: "",
        accessorKey: "logDate",
        cell: LogTimeCell,
        ...DATE_COLUMN_SIZE,
    },
    [HistoryInvoiceTemplateDetailsColumn.TEMPLATE_NAME]: {
        name: "Template name",
        header: "Template name",
        accessorKey: "templateName",
        minSize: 260,
        maxSize: 400,
    },
};

export const createHistoryInvoiceTemplateChangeViewConfig = (
    data: HistoryInvoiceTemplateChangeRow[],
): ViewConfig<
    HistoryInvoiceTemplateChangeRow,
    HistoryInvoiceTemplateChangeColumn,
    never
> => {
    const singleField = getSingleFieldLabel(data);
    const previousLabel = singleField
        ? `Previous ${singleField}`
        : DEFAULT_PREVIOUS_LABEL;
    const modifiedLabel = singleField
        ? `Modified ${singleField}`
        : DEFAULT_MODIFIED_LABEL;

    return {
        columns: {
            ...baseColumns,
            [HistoryInvoiceTemplateDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                minSize: 280,
                maxSize: 420,
            },
            [HistoryInvoiceTemplateDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                minSize: 280,
                maxSize: 420,
            },
        },
    };
};

export const createHistoryInvoiceTemplateContentViewConfig = (): ViewConfig<
    HistoryInvoiceTemplateContentRow,
    HistoryInvoiceTemplateContentColumn,
    never
> => ({
    columns: {
        ...baseColumns,
        [HistoryInvoiceTemplateDetailsColumn.CHANGE]: {
            name: "Change",
            header: "Change",
            accessorKey: "change",
            minSize: 280,
            maxSize: 420,
        },
    },
});
